import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg, ROLE_LABEL, dt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Sel, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const admin = user.role === "admin";
  const save = async () => {
    try {
      const body = { ...edit, password: edit.password || null };
      if (edit.id) await api.put(`/users/${edit.id}`, body); else await api.post("/users", body);
      toast.success("Usuário salvo"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="animate-in fade-in duration-500">
      <PageHeader title="Equipe" subtitle="Administradores, gerentes e corretores da imobiliária. O limite de usuários depende do plano.">
        {admin && <Button onClick={() => setEdit({ name: "", email: "", password: "", role: "broker", phone: "", code: "", active: true })} data-testid="new-user-button"><Plus className="h-4 w-4 mr-2" />Novo usuário</Button>}
      </PageHeader>
      <div className="card-lux p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b"><th className="p-3">Nome</th><th>E-mail</th><th>Perfil</th><th>Código</th><th>WhatsApp</th><th>Status</th><th>Último acesso</th><th /></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-b last:border-0" data-testid={`user-row-${u.email}`}>
              <td className="p-3 font-medium">{u.name}</td><td>{u.email}</td><td>{ROLE_LABEL[u.role]}</td><td className="font-mono text-xs">{u.code}</td><td className="font-mono text-xs">{u.phone || "—"}</td>
              <td>{u.active ? <span className="text-emerald-600 text-xs">Ativo</span> : <span className="text-muted-foreground text-xs">Inativo</span>}</td><td className="text-xs">{dt(u.last_login_at)}</td>
              <td>{admin && <Button size="sm" variant="ghost" onClick={() => setEdit({ ...u, password: "" })} data-testid={`edit-user-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && (
        <Dialog open onOpenChange={() => setEdit(null)}>
          <DialogContent className="bg-background">
            <DialogHeader><DialogTitle>{edit.id ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nome"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="user-name-input" /></Field>
              <Field label="E-mail"><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="user-email-input" /></Field>
              <Field label={edit.id ? "Nova senha (opcional)" : "Senha (mín. 8)"}><Input type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} data-testid="user-password-input" /></Field>
              <Field label="Perfil"><Sel value={edit.role} onChange={(v) => setEdit({ ...edit, role: v })} options={{ admin: "Administrador", manager: "Gerente", broker: "Corretor" }} testid="user-role-select" className="w-full" /></Field>
              <Field label="WhatsApp (DDI+DDD)"><Input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="user-phone-input" /></Field>
              <Field label="Código (links /corretor/)"><Input value={edit.code || ""} onChange={(e) => setEdit({ ...edit, code: e.target.value })} data-testid="user-code-input" /></Field>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="user-active-switch" />Ativo</label>
            </div>
            <DialogFooter><Button onClick={save} data-testid="user-save-button">Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
